import React, { FC, memo, useMemo } from 'react';
import { useTheme } from '@mui/material';
import moment from 'moment-timezone';

import { IDepartmentScheduleDTO } from 'src/api/dtos';
import { EmployeesIcon } from 'src/assets/img/icons';
import { useTranslate } from 'src/shared/providers';
import { getFirstDayOfMonthByDate } from 'src/shared/utils';

import { MonthlyEventType } from './MonthlyCalendar';
import { MonthlyEventList } from './MonthlyEventList';
import './MonthlyCell.styles.scss';

interface Props {
  day: moment.Moment;
  events: MonthlyEventType[];
  disabled: boolean;
  showDays: boolean;
  isLastCellInGrid: boolean;
  departmentSchedule: IDepartmentScheduleDTO[] | null;
  onSelectDate?: (date: string) => void;
}

export const MonthlyCell: FC<Props> = memo(
  ({ day, events, disabled, showDays, isLastCellInGrid, departmentSchedule, onSelectDate }) => {
    const { t } = useTranslate();
    const theme = useTheme();
    const hasUnpublished = useMemo(() => events?.some((event) => event.unpublished), [events]);
    const unpublishedClass = hasUnpublished ? 'unpublished' : '';
    const disabledStyle = disabled ? 'disabled' : '';

    return (
      <div className={`monthly-cell ${theme.palette.mode} ${unpublishedClass} ${disabledStyle}`}>
        {showDays && <p className="mb-0 text-uppercase d-block day-label padding-x">{day.format('ddd')}</p>}
        <span className="day-number padding-x d-flex">
          <span>
            {getFirstDayOfMonthByDate(day).isSame(day, 'day') && <span className="me-1">{day.format('MMM')}</span>}
            {day.format('DD')}
          </span>
          {departmentSchedule && !!departmentSchedule.length && (
            <span className="employee-wrapper" onClick={() => onSelectDate?.(departmentSchedule[0].date)}>
              +{departmentSchedule.length} <EmployeesIcon fill={theme.palette.primary.main} />
            </span>
          )}
        </span>
        <MonthlyEventList events={events} day={day} isLastCellInGrid={isLastCellInGrid} />
        {hasUnpublished && (
          <div className="daily-not-published">
            <div className="shadow-sm">{t('calendar.notPublished')}</div>
          </div>
        )}
      </div>
    );
  },
);
